// Configuration / preference / settings thunks extracted from
// Services/ServicesThunks.js. Public API:
//   updateConfigurationData
//   updatePreferenceData
//   updateSettings
//
// Private helpers (writeConfigDataToCache, updateCustomCoursesCacheOnReset,
// dispatchAttendanceAutoMarkingMutation, segregateSettingsByType,
// buildMutationInput, handlePostUpdate, applyOptimisticConfigurationUpdate,
// updateSettingsV2) live alongside the public thunks because they have no
// other consumer.

import _ from "lodash";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import { dispatchError } from "Utils";
import {
  getConfigurationsFromCacheV2,
  writeConfigurationsToCacheV2,
  readConfigurationFragmentV2,
  getPreferencesFromCacheV2,
  writePreferencesToCacheV2,
} from "ChildrenPage/modules/ChildrenPageGraphqlHelpers";
import { setToastMsg, getUserInfo } from "Login/modules/LoginModule";
import ACLStore from "lib/aclStore";
import { bulkUpdateConfigurationMutation } from "../QueryMutationFragment/CommonQueryMutationFragment/mutations";
import {
  getConfigurationQuery,
  getPreferenceQuery,
} from "ChildrenPage/modules/ChildrenPageQuery";
import { getCurriculumCustomCoursesQuery } from "ConfigurationsV2/modules/ConfigurationQuery";
import {
  generateConfigurationMutationVariables,
  generateConfigurationQueryVariables,
} from "ChildrenPage/modules/ChildrenPageUtils";
import {
  ORGANIZATION_LEVEL,
  UPDATE_USER_SETTINGS,
  ENABLE_CONFIGURATION_FETCHING_OPTIMISATION,
} from "Constants/permissionConstants";
import {
  getConfigurationFromCache,
  readConfigurationForUpdate,
  writeConfigurationForUpdate,
} from "Utils/ConfigurationUtils/GraphqlHelpers";
import {
  getConfigurationContexts,
  generateConfigurationQueryVariablesV2,
  getConfigurationAdditionalVariables,
} from "Utils/ConfigurationUtils/Utils";
import {
  getConfigurationQuery as getConfigurationQueryV2,
  getPreferenceQuery as getPreferenceQueryV2,
} from "Utils/ConfigurationUtils/Queries";
import { NOTIFICATION_COLUMN_ID_OBJECT } from "UIComponents/ConfigurationsV2/utils.js";
import {
  getCurriculumCustomCoursesFromCache,
  writeCurriculumCustomCoursesToCache,
} from "ConfigurationsV2/modules/ConfigurationGraphqlHelpers";
import { updateAttendanceAutoMarkingSettingsMutation } from "Attendance/modules/AttendanceMutations";
import { getAttendanceAutoMarkingNewFlowEnabled } from "AttendanceDashboard/modules/Utils";

//Updates cache according to config type
const writeConfigDataToCache = (
  configType,
  configurationQueryVariables,
  data
) => {
  if (configType === "preferences") {
    writePreferencesToCacheV2(configurationQueryVariables, data);
  } else {
    writeConfigurationsToCacheV2(configurationQueryVariables, data);
  }
};

const updateCustomCoursesCacheOnReset = ({
  input,
  refetchCustomCourses,
  curriculumProgramId,
  customCoursesQueryVariables,
}) => {
  const isResetCourseConfigurationTags = _.isEqual(
    _.get(_.first(input), "configureCustomCourseTags", ""),
    "RESET_CURRICULUM_LEVEL_TAGS"
  );

  if (
    !isResetCourseConfigurationTags ||
    !refetchCustomCourses ||
    _.isEmpty(curriculumProgramId)
  )
    return;

  const customCoursesResetIds = input.map(({ id }) => id);
  const customCoursesResponse = getCurriculumCustomCoursesFromCache(
    customCoursesQueryVariables
  );

  const curriculumPrograms = _.get(
    customCoursesResponse,
    "curriculumPrograms",
    []
  );
  const curriculumProgramIndex = _.findIndex(curriculumPrograms, {
    id: curriculumProgramId,
  });

  let updatedCustomCourses = { ...customCoursesResponse };
  customCoursesResetIds.forEach(resetId => {
    updatedCustomCourses = update(updatedCustomCourses, {
      curriculumPrograms: {
        [curriculumProgramIndex]: {
          moduleSettingCourseCusomTags: {
            customCourseData: {
              [resetId]: { $set: [] },
            },
          },
        },
      },
    });
  });

  writeCurriculumCustomCoursesToCache({
    data: updatedCustomCourses,
    variables: customCoursesQueryVariables,
  });
};

const dispatchAttendanceAutoMarkingMutation = async ({
  input,
  academicYearId,
  curriculumProgramId,
  isAutoMarkingConfiguration,
}) => {
  if (!getAttendanceAutoMarkingNewFlowEnabled() || !isAutoMarkingConfiguration)
    return;

  await client.mutate({
    mutation: updateAttendanceAutoMarkingSettingsMutation,
    variables: {
      input: {
        academicYearId,
        curriculumProgramId,
        value: _.isEqual("true", _.get(_.first(input), "value")),
      },
    },
  });
};

export const updateConfigurationData = ({
  input,
  optimisticUpdate = true,
  refetchConfiguration = true,
  refetchCustomCourses = false,
  configurationQueryVariables,
  customCoursesQueryVariables,
  academicYearId = null,
  curriculumProgramId = null,
  isAutoMarkingConfiguration,
} = {}) => {
  return async dispatch => {
    const oldData = getConfigurationsFromCacheV2(configurationQueryVariables);

    const configurations = _.get(oldData, "0.configurations", []);

    try {
      const hasIdProperty = _.every(input, obj => !_.isEmpty(_.get(obj, "id")));
      if (!hasIdProperty) throw new Error("");

      if (optimisticUpdate) {
        const updatedConfigurations = _.map(configurations, _configuration => {
          const matchedConfigurations = _.filter(input, {
            id: _configuration.id,
          });
          if (_.isEmpty(matchedConfigurations)) return _configuration;
          return applyOptimisticConfigurationUpdate(
            _configuration,
            matchedConfigurations
          );
        });

        const newData = {
          platform: {
            __typename: "PlatformQuery",
            configuration: [
              {
                configurations: updatedConfigurations,
                __typename: "ConfigurationResponse",
              },
            ],
          },
        };

        //update cache based on configuration type
        writeConfigDataToCache(
          "configuration",
          configurationQueryVariables,
          newData
        );
      }

      updateCustomCoursesCacheOnReset({
        input,
        refetchCustomCourses,
        curriculumProgramId,
        customCoursesQueryVariables,
      });

      const refetchQueries = refetchConfiguration
        ? {
            refetchQueries: [
              {
                query: getConfigurationQuery,
                variables: configurationQueryVariables,
              },
            ],
          }
        : { refetchQueries: [] };
      refetchCustomCourses
        ? refetchQueries.refetchQueries.push({
            query: getCurriculumCustomCoursesQuery,
            variables: customCoursesQueryVariables,
          })
        : null;

      const res = await client.mutate({
        mutation: bulkUpdateConfigurationMutation,
        variables: { input },
        ...refetchQueries,
      });

      await dispatchAttendanceAutoMarkingMutation({
        input,
        academicYearId,
        curriculumProgramId,
        isAutoMarkingConfiguration,
      });

      const setMultipleConfigurationValueForEntityGroups = _.get(
        res,
        "data.platform.setMultipleConfigurationValueForEntityGroups",
        false
      );
      if (!setMultipleConfigurationValueForEntityGroups) {
        // resetting the cache to older data if the setMultipleConfigurationValueForEntityGroups is not true
        const oldData = {
          platform: {
            __typename: "PlatformQuery",
            configuration: [
              {
                configurations: configurations,
                __typename: "ConfigurationResponse",
              },
            ],
          },
        };

        //update cache based on configuration type
        writeConfigDataToCache(
          "configuration",
          configurationQueryVariables,
          oldData
        );
      }
      return res;
    } catch (e) {
      if (optimisticUpdate) {
        const oldData = {
          platform: {
            __typename: "PlatformQuery",
            configuration: [
              {
                configurations: configurations,
                __typename: "ConfigurationResponse",
              },
            ],
          },
        };

        //update cache based on configuration type
        writeConfigDataToCache(
          "configuration",
          configurationQueryVariables,
          oldData
        );
      }
      console.error(e, "Services.js error occoured");
      dispatchError({ dispatch, e });
    }
  };
};

export const updatePreferenceData = ({
  input,
  optimisticUpdate = true,
  refetchConfiguration = true,
  refetchCustomCourses = false,
  configurationQueryVariables,
  customCoursesQueryVariables,
  academicYearId = null,
  curriculumProgramId = null,
  isAutoMarkingConfiguration,
} = {}) => {
  return async dispatch => {
    const oldData = getPreferencesFromCacheV2(configurationQueryVariables);

    const configurations = _.get(oldData, "0.configurations", []);

    try {
      const hasIdProperty = _.every(input, obj => !_.isEmpty(_.get(obj, "id")));
      if (!hasIdProperty) throw new Error("");

      if (optimisticUpdate) {
        const updatedConfigurations = _.map(configurations, _configuration => {
          const matchedConfigurations = _.filter(input, {
            id: _configuration.id,
          });
          if (_.isEmpty(matchedConfigurations)) return _configuration;
          return applyOptimisticConfigurationUpdate(
            _configuration,
            matchedConfigurations
          );
        });

        const newData = {
          platform: {
            __typename: "PlatformQuery",
            preferences: [
              {
                configurations: updatedConfigurations,
                __typename: "ConfigurationResponse",
              },
            ],
          },
        };

        //update cache based on configuration type
        writeConfigDataToCache(
          "preferences",
          configurationQueryVariables,
          newData
        );
      }

      updateCustomCoursesCacheOnReset({
        input,
        refetchCustomCourses,
        curriculumProgramId,
        customCoursesQueryVariables,
      });

      const refetchQueries = refetchConfiguration
        ? {
            refetchQueries: [
              {
                query: getPreferenceQuery,
                variables: configurationQueryVariables,
              },
            ],
          }
        : { refetchQueries: [] };
      refetchCustomCourses
        ? refetchQueries.refetchQueries.push({
            query: getCurriculumCustomCoursesQuery,
            variables: customCoursesQueryVariables,
          })
        : null;

      const res = await client.mutate({
        mutation: bulkUpdateConfigurationMutation,
        variables: { input },
        ...refetchQueries,
      });

      await dispatchAttendanceAutoMarkingMutation({
        input,
        academicYearId,
        curriculumProgramId,
        isAutoMarkingConfiguration,
      });

      const setMultipleConfigurationValueForEntityGroups = _.get(
        res,
        "data.platform.setMultipleConfigurationValueForEntityGroups",
        false
      );
      if (!setMultipleConfigurationValueForEntityGroups) {
        // resetting the cache to older data if the setMultipleConfigurationValueForEntityGroups is not true
        const oldData = {
          platform: {
            __typename: "PlatformQuery",
            preferences: [
              {
                configurations: configurations,
                __typename: "ConfigurationResponse",
              },
            ],
          },
        };

        //update cache based on configuration type
        writeConfigDataToCache(
          "preferences",
          configurationQueryVariables,
          oldData
        );
      }
      return res;
    } catch (e) {
      if (optimisticUpdate) {
        const oldData = {
          platform: {
            __typename: "PlatformQuery",
            preferences: [
              {
                configurations: configurations,
                __typename: "ConfigurationResponse",
              },
            ],
          },
        };

        //update cache based on configuration type
        writeConfigDataToCache(
          "preferences",
          configurationQueryVariables,
          oldData
        );
      }
      console.error(e, "Services.js error occoured");
      dispatchError({ dispatch, e });
    }
  };
};

const segregateSettingsByType = (settings, readConfigFn) => {
  const preferenceUpdateList = [];
  const configUpdateList = [];

  _.forEach(settings, setting => {
    const configuration = readConfigFn(_.get(setting, "name"));
    // Guard: getConfigurationFromCache returns {} (truthy) when not found,
    // so check configuration?.id not just !configuration
    if (!configuration?.id) return;

    const updateItem = {
      configuration,
      newValue: setting.value,
    };

    if (_.get(configuration, "aclControlParameterType") === "PREFERENCE") {
      preferenceUpdateList.push(updateItem);
    } else {
      configUpdateList.push(updateItem);
    }
  });

  return { preferenceUpdateList, configUpdateList };
};

const buildMutationInput = (settingList, mutationParams) => {
  const {
    mutationVariablesSrc,
    currentCurriculumProgramId,
    courseId,
    identityId,
    learningCourseId,
  } = mutationParams;

  return generateConfigurationMutationVariables({
    src: mutationVariablesSrc,
    configUpdateList: settingList,
    shouldStringifyValue: false,
    currentCurriculumProgramId,
    courseId,
    identityId,
    learningCourseId,
  });
};

const handlePostUpdate = (
  dispatch,
  { showToastMsg, customToastMessage, onSettingsUpdate }
) => {
  if (showToastMsg) {
    dispatch(
      setToastMsg({
        type: "success",
        msg: customToastMessage || "toastMsgs:saved_successfully",
      })
    );
  }
  onSettingsUpdate?.();
};

const applyOptimisticConfigurationUpdate = (
  configuration,
  matchedMutationInputs
) => {
  const entityIdMapping = {
    IN_APP: "inApp",
    PUSH: "push",
    EMAIL: "email",
  };

  const newPlatformCategorizedValues = _.reduce(
    matchedMutationInputs,
    (acc, input) => {
      const entityGroups = _.get(input, "entityGroups.0");
      const userTypeEntityGroup = _.find(entityGroups, group =>
        _.isEqual(group.entityType, "USER_TYPE")
      );
      const entityId = _.get(userTypeEntityGroup, "entityId");
      return userTypeEntityGroup ? { ...acc, [entityId]: input.value } : acc;
    },
    {}
  );

  const updatedConfiguration = { ...configuration };

  if (_.size(newPlatformCategorizedValues) > 0) {
    updatedConfiguration.platformCategorizedValues = {
      ..._.get(configuration, "platformCategorizedValues", {}),
      ...newPlatformCategorizedValues,
    };
  } else {
    const newValue = _.get(_.first(matchedMutationInputs), "value");
    updatedConfiguration.value = newValue;

    const entityGroups = _.get(_.first(matchedMutationInputs), "entityGroups");

    if (_.size(entityGroups) > 1) {
      updatedConfiguration.channelTypeCategorizedValues = _.mapValues(
        NOTIFICATION_COLUMN_ID_OBJECT,
        () => newValue
      );
    } else {
      const columnId = _.filter(_.first(entityGroups), entity => {
        return _.has(
          NOTIFICATION_COLUMN_ID_OBJECT,
          _.get(entityIdMapping, _.get(entity, "entityId"))
        );
      });
      const platformType = _.get(
        entityIdMapping,
        _.get(_.first(columnId), "entityId")
      );
      updatedConfiguration.channelTypeCategorizedValues = {
        ..._.get(configuration, "channelTypeCategorizedValues", {}),
        [platformType]: newValue,
      };
    }
  }

  return updatedConfiguration;
};

const updateSettingsV2 = ({
  settings,
  showToastMsg = true,
  onSettingsUpdate,
  currentCurriculumProgramId,
  queryVariablesSrc = ORGANIZATION_LEVEL,
  mutationVariablesSrc = UPDATE_USER_SETTINGS,
  courseId,
  portalType = "PLANNER",
  customToastMessage,
  learningCourseId,
  refetchCustomCourses = false,
  customCoursesQueryVariables,
  academicYearId = null,
  curriculumProgramId = null,
  isAutoMarkingConfiguration,
}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });

    const context = getConfigurationContexts({
      src: queryVariablesSrc,
      metadata: {
        organizationId: userInfo.org_id,
        currentCurriculumProgramId,
        courseId,
        learningCourseId,
        userId: userInfo.id,
        identityId: _.get(userInfo, "identityId"),
      },
    });

    const additionalVariables = getConfigurationAdditionalVariables({
      src: queryVariablesSrc,
    });

    const {
      preferenceUpdateList,
      configUpdateList,
    } = segregateSettingsByType(settings, settingName =>
      getConfigurationFromCache({ context, configurationName: settingName })
    );

    const mutationParams = {
      mutationVariablesSrc,
      currentCurriculumProgramId,
      courseId,
      identityId: _.get(userInfo, "identityId"),
      learningCourseId,
    };

    const allUpdateItems = [...preferenceUpdateList, ...configUpdateList];
    let preferenceInput = [];
    let configInput = [];

    if (!_.isEmpty(preferenceUpdateList)) {
      preferenceInput = buildMutationInput(
        preferenceUpdateList,
        mutationParams
      );
    }
    if (!_.isEmpty(configUpdateList)) {
      configInput = buildMutationInput(configUpdateList, mutationParams);
    }

    const allMutationInputs = [...preferenceInput, ...configInput];

    const snapshots = _.map(allUpdateItems, ({ configuration }) => ({
      configurationName: configuration.configuration,
      originalConfiguration: readConfigurationForUpdate({
        context,
        configurationName: configuration.configuration,
        fragmentVariables: additionalVariables,
      }),
    }));

    _.forEach(allUpdateItems, ({ configuration }) => {
      const matchedInputs = _.filter(allMutationInputs, {
        id: configuration.id,
      });
      if (_.isEmpty(matchedInputs)) return;

      const updatedConfiguration = applyOptimisticConfigurationUpdate(
        configuration,
        matchedInputs
      );

      writeConfigurationForUpdate({
        cache: client.cache,
        context,
        configurationName: configuration.configuration,
        data: updatedConfiguration,
        fragmentVariables: additionalVariables,
      });
    });

    try {
      const mutationPromises = [];

      if (!_.isEmpty(preferenceInput)) {
        mutationPromises.push(
          client.mutate({
            mutation: bulkUpdateConfigurationMutation,
            variables: { input: preferenceInput },
          })
        );
      }

      if (!_.isEmpty(configInput)) {
        mutationPromises.push(
          client.mutate({
            mutation: bulkUpdateConfigurationMutation,
            variables: { input: configInput },
          })
        );
      }

      await Promise.all(mutationPromises);

      updateCustomCoursesCacheOnReset({
        input: configInput,
        refetchCustomCourses,
        curriculumProgramId: currentCurriculumProgramId,
        customCoursesQueryVariables,
      });

      await dispatchAttendanceAutoMarkingMutation({
        input: configInput,
        academicYearId,
        curriculumProgramId: currentCurriculumProgramId,
        isAutoMarkingConfiguration,
      });

      const refetchPromises = [];

      if (!_.isEmpty(configUpdateList)) {
        const configVariables = generateConfigurationQueryVariablesV2({
          src: queryVariablesSrc,
          configurations: _.map(
            configUpdateList,
            ({ configuration }) => configuration.configuration
          ),
          currentCurriculumProgramId,
          courseId,
          organizationId: userInfo.org_id,
          learningCourseId,
        });
        refetchPromises.push(
          client.query({
            query: getConfigurationQueryV2,
            variables: configVariables,
            fetchPolicy: "network-only",
          })
        );
      }

      if (!_.isEmpty(preferenceUpdateList)) {
        const preferenceVariables = generateConfigurationQueryVariablesV2({
          src: queryVariablesSrc,
          configurations: _.map(
            preferenceUpdateList,
            ({ configuration }) => configuration.configuration
          ),
          currentCurriculumProgramId,
          courseId,
          organizationId: userInfo.org_id,
          learningCourseId,
        });
        refetchPromises.push(
          client.query({
            query: getPreferenceQueryV2,
            variables: preferenceVariables,
            fetchPolicy: "network-only",
          })
        );
      }

      if (refetchCustomCourses) {
        refetchPromises.push(
          client.query({
            query: getCurriculumCustomCoursesQuery,
            variables: customCoursesQueryVariables,
            fetchPolicy: "network-only",
          })
        );
      }

      await Promise.all(refetchPromises);

      handlePostUpdate(dispatch, {
        showToastMsg,
        customToastMessage,
        onSettingsUpdate,
      });
      return true;
    } catch (e) {
      console.error(e);
      _.forEach(snapshots, ({ configurationName, originalConfiguration }) => {
        writeConfigurationForUpdate({
          cache: client.cache,
          context,
          configurationName,
          data: originalConfiguration,
          fragmentVariables: additionalVariables,
        });
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return false;
    }
  };
};

export const updateSettings = ({
  settings,
  showToastMsg = true,
  onSettingsUpdate,
  currentCurriculumProgramId,
  queryVariablesSrc = ORGANIZATION_LEVEL,
  mutationVariablesSrc = UPDATE_USER_SETTINGS,
  courseId,
  portalType = "PLANNER",
  refetchConfiguration = true,
  customToastMessage,
  learningCourseId,
  refetchCustomCourses = false,
  customCoursesQueryVariables,
  academicYearId = null,
  curriculumProgramId = null,
  isAutoMarkingConfiguration,
}) => {
  return async (dispatch, getState) => {
    const isOptimisationEnabled = ACLStore.can(
      ENABLE_CONFIGURATION_FETCHING_OPTIMISATION
    );

    if (isOptimisationEnabled) {
      // refetchConfiguration is intentionally not forwarded: V2 always performs a
      // targeted refetch (only updated config names) which supersedes the legacy
      // full-query refetch. Callers passing refetchConfiguration: false still get
      // an optimistic update + lightweight targeted refetch in the V2 path.
      return dispatch(
        updateSettingsV2({
          settings,
          showToastMsg,
          onSettingsUpdate,
          currentCurriculumProgramId,
          queryVariablesSrc,
          mutationVariablesSrc,
          courseId,
          portalType,
          customToastMessage,
          learningCourseId,
          refetchCustomCourses,
          customCoursesQueryVariables,
          academicYearId,
          curriculumProgramId,
          isAutoMarkingConfiguration,
        })
      );
    }

    const userInfo = getUserInfo({ portalType });
    try {
      const configurationQueryVariables = generateConfigurationQueryVariables({
        src: queryVariablesSrc,
        currentCurriculumProgramId,
        courseId,
        organizationId: userInfo.org_id,
        learningCourseId,
      });

      const {
        preferenceUpdateList,
        configUpdateList,
      } = segregateSettingsByType(settings, settingName =>
        readConfigurationFragmentV2(configurationQueryVariables, settingName)
      );

      const mutationParams = {
        mutationVariablesSrc,
        currentCurriculumProgramId,
        courseId,
        identityId: _.get(userInfo, "identityId"),
        learningCourseId,
      };

      const promises = [];

      if (!_.isEmpty(preferenceUpdateList)) {
        const input = buildMutationInput(preferenceUpdateList, mutationParams);
        promises.push(
          dispatch(
            updatePreferenceData({
              input,
              configurationQueryVariables,
              refetchConfiguration,
            })
          )
        );
      }

      if (!_.isEmpty(configUpdateList)) {
        const input = buildMutationInput(configUpdateList, mutationParams);
        promises.push(
          dispatch(
            updateConfigurationData({
              input,
              configurationQueryVariables,
              refetchConfiguration,
            })
          )
        );
      }

      await Promise.all(promises);

      handlePostUpdate(dispatch, {
        showToastMsg,
        customToastMessage,
        onSettingsUpdate,
      });
      return true;
    } catch (e) {
      console.error(e);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return false;
    }
  };
};
